import { LuCircleAlert, LuPlus, LuX } from 'react-icons/lu';

import { Account, GUID, PortfolioEngineAccount } from '@ndvr/portfolio-api';

import { EllipsisTooltip } from '@/components/ui/EllipsisTooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { titleCase } from 'utils/string-utils';

export interface WizardExcludedAccount {
    account: PortfolioEngineAccount;
    reason?: string;
    canRestore: boolean;
}

interface Props {
    accounts: PortfolioEngineAccount[];
    excludedAccounts?: WizardExcludedAccount[];
    onExcludeAccount?: (accountGUID: GUID<Account>) => void;
    onIncludeAccount?: (accountGUID: GUID<Account>) => void;
}

export const WizardAccountsList = ({ accounts, excludedAccounts = [], onExcludeAccount, onIncludeAccount }: Props) => {
    return (
        <div className="flex flex-col gap-6 px-4 py-4">
            <AccountListSection
                title="Accounts"
                entries={accounts.map((account) => ({ account }))}
                emptyText="No accounts included."
                action={
                    onExcludeAccount
                        ? {
                              icon: LuX,
                              label: (account) => `Exclude ${account.accountNamePlain ?? account.accountGUID}`,
                              tooltip: 'Exclude from onboarding',
                              onClick: onExcludeAccount,
                          }
                        : undefined
                }
            />
            {excludedAccounts.length > 0 && (
                <AccountListSection
                    title="Excluded accounts"
                    entries={excludedAccounts}
                    emptyText="No excluded accounts."
                    muted
                    action={
                        onIncludeAccount
                            ? {
                                  icon: LuPlus,
                                  label: (account) => `Include ${account.accountNamePlain ?? account.accountGUID}`,
                                  onClick: onIncludeAccount,
                                  isVisible: (entry) => entry.canRestore === true,
                              }
                            : undefined
                    }
                />
            )}
        </div>
    );
};

interface AccountListEntry {
    account: PortfolioEngineAccount;
    reason?: string;
    canRestore?: boolean;
}

interface AccountListSectionProps {
    title: string;
    entries: AccountListEntry[];
    emptyText: string;
    muted?: boolean;
    action?: {
        icon: React.ComponentType<{ className?: string }>;
        label: (account: PortfolioEngineAccount) => string;
        onClick: (accountGUID: GUID<Account>) => void;
        tooltip?: string;
        isVisible?: (entry: AccountListEntry) => boolean;
    };
}

type AccountAction = NonNullable<AccountListSectionProps['action']>;

const AccountActionButton = ({
    action,
    account,
    ActionIcon,
}: {
    action: AccountAction;
    account: PortfolioEngineAccount;
    ActionIcon: React.ComponentType<{ className?: string }>;
}) => {
    const button = (
        <button
            type="button"
            onClick={() => action.onClick(account.accountGUID)}
            aria-label={action.label(account)}
            className="text-cocoa-500 hover:text-cocoa-1000 hover:bg-cocoa-100 invisible absolute top-1/2 right-0 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded transition group-hover/account:visible"
        >
            <ActionIcon className="h-4 w-4" />
        </button>
    );

    if (!action.tooltip) return button;

    return (
        <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent>{action.tooltip}</TooltipContent>
        </Tooltip>
    );
};

const AccountListSection = ({ title, entries, emptyText, muted = false, action }: AccountListSectionProps) => {
    const ActionIcon = action?.icon;

    return (
        <section className="flex flex-col gap-2">
            {muted ? (
                <div className="flex items-center gap-2">
                    <span className="text-cocoa-400 text-xs font-medium tracking-wide uppercase">
                        {title} ({entries.length})
                    </span>
                    <div className="border-cocoa-200 flex-1 border-t" />
                </div>
            ) : (
                <h3 className="font-heading text-cocoa-1000 text-lg font-medium">
                    {title} <span className="text-cocoa-500 text-sm font-normal">({entries.length})</span>
                </h3>
            )}
            {entries.length === 0 ? (
                <p className="text-cocoa-500 text-sm italic">{emptyText}</p>
            ) : (
                <ul className="divide-cocoa-100 divide-y">
                    {entries.map((entry) => {
                        const { account, reason } = entry;
                        const isAutoExcluded = muted && entry.canRestore === false;
                        const showAction = !!action && !!ActionIcon && (action.isVisible?.(entry) ?? true);

                        return (
                            <li
                                key={account.accountGUID}
                                className="group/account relative flex items-center justify-between gap-2 py-1.5 text-sm"
                            >
                                <span className="flex min-w-0 flex-1 items-center gap-1.5">
                                    <EllipsisTooltip
                                        className={
                                            isAutoExcluded
                                                ? 'text-cocoa-400'
                                                : muted
                                                  ? 'text-cocoa-600'
                                                  : 'text-cocoa-1000'
                                        }
                                    >
                                        {account.accountNamePlain ?? '—'}
                                    </EllipsisTooltip>
                                    {isAutoExcluded && reason && (
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <button
                                                    type="button"
                                                    className="text-cocoa-400 hover:text-cocoa-600 shrink-0 transition"
                                                    aria-label="Why is this account excluded?"
                                                >
                                                    <LuCircleAlert className="h-3.5 w-3.5" />
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent
                                                side="right"
                                                className="text-cocoa-700 text-xs leading-relaxed"
                                            >
                                                {reason}
                                            </PopoverContent>
                                        </Popover>
                                    )}
                                </span>
                                {!isAutoExcluded && (
                                    <span
                                        className={`text-cocoa-500 w-20 shrink-0 text-right text-xs ${
                                            showAction ? 'group-hover/account:invisible' : ''
                                        }`}
                                    >
                                        <div className="tabular-nums">{account.custodyAccountId ?? '—'}</div>
                                        {account.custodyType && <div>{titleCase(account.custodyType)}</div>}
                                    </span>
                                )}
                                {showAction && (
                                    <AccountActionButton action={action} account={account} ActionIcon={ActionIcon} />
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};
